import React, {
  useState, useCallback, useRef, useEffect,
} from 'react'
import { Captcha, FloatingInput, FloatingPasswordInput } from '@usap/uicomponents'
import useUserAPIController from '../../../../../apiControllers/user.apicontroller'
import useInvisibleRecaptcha from '../../../../../hooks/useInvisibleRecaptcha'
import captchaConfig from '../../../../../../config/captcha-config'
import GTM from '../../../../../helpers/GTM'
import BenefitsBlock from './BenefitsBlock'
import LoyaltySocialButtons from './LoyaltySocialButtons'
import LoyaltyErrorToast from './LoyaltyErrorToast'
import {
  ModalBody,
  FieldsStack,
  PrimaryButton,
  ForgotPasswordLink,
  Divider,
  SectionHeading,
} from './LoyaltySignInModal.css'

type SignInFormProps = {
  onSuccess: (tokenDb: any) => void
  onSwitchToCreate: () => void
}

const SignInForm: React.FC<SignInFormProps> = ({ onSuccess, onSwitchToCreate }) => {
  const { cartLoginCtaEngagement, cartSignUpCtaEngagement, cartLoginGoogleCtaEngagement } = GTM as any
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [captcha, setCaptcha] = useState<string | false>(false)

  // Track which field has focus so FloatingInput / FloatingPasswordInput can
  // render the appropriate focused state (clear-icon visible, label floated, etc.)
  // Mirrors LoginFormWMO's pattern.
  const [focusedInput, setFocusedInput] = useState('')
  const onFocusFun = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => setFocusedInput(e.target.name),
    [],
  )
  const onBlurFun = useCallback(() => setFocusedInput(''), [])

  // Ref to the @usap/uicomponents <Captcha> wrapper. It exposes
  // resetCaptcha / executeRecaptchaSync via useImperativeHandle — the raw
  // react-google-recaptcha does NOT (that's the source of the f42=b crash
  // this fix addresses). Typed `any` because the wrapper's TS types aren't
  // published; matches the TrackPackage UnlockRewardsModal usage.
  const inVisibleRecaptchaRef = useRef<any>(null)
  const isInvRecaptchaEnabled = useInvisibleRecaptcha()
  const { userLogin } = useUserAPIController()

  const canSubmit = email.trim().length > 0 && password.length > 0

  const doSignIn = useCallback(async (token: string) => {
    setLoading(true)
    setError('')
    try {
      const tokenDb = await userLogin({
        loginEmail: email,
        loginPassword: password,
        captcha: token,
        isInvRecaptchaEnabled,
      }, '')
      onSuccess(tokenDb)
    } catch (err: any) {
      // Login API rejects with axios error. 404 / 401 bodies are shaped
      // `{ errors: [{ message: "..." }], requestId: "..." }` — read errors[0]
      // first; fall through to the older single-message shape, then the axios
      // default, then a generic.
      const msg = err?.response?.data?.errors?.[0]?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Invalid email or password. Please try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [email, password, isInvRecaptchaEnabled, userLogin, onSuccess])

  useEffect(() => {
    if (captcha && isInvRecaptchaEnabled) {
      doSignIn(captcha as string).then(() => setCaptcha(false))
    }
  }, [captcha, isInvRecaptchaEnabled, doSignIn])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    cartLoginCtaEngagement()
    if (!canSubmit) return
    if (isInvRecaptchaEnabled) {
      inVisibleRecaptchaRef.current?.resetCaptcha()
      // eslint-disable-next-line no-sync
      inVisibleRecaptchaRef.current?.executeRecaptchaSync()
      return
    }
    await doSignIn(captcha as string)
  }, [canSubmit, captcha, isInvRecaptchaEnabled, doSignIn, cartLoginCtaEngagement])

  const onEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
    [],
  )
  const onPasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value),
    [],
  )
  const resetEmail = useCallback(() => setEmail(''), [])
  const resetPassword = useCallback(() => setPassword(''), [])

  const handleGoogleEngagement = useCallback(() => cartLoginGoogleCtaEngagement({ buttonText: 'Continue With Google', form: 'Sign In' }), [cartLoginGoogleCtaEngagement])
  const handleSwitchToCreate = useCallback(() => {
    cartSignUpCtaEngagement()
    onSwitchToCreate()
  }, [cartSignUpCtaEngagement, onSwitchToCreate])

  return (
    <ModalBody as="form" onSubmit={handleSubmit}>
      <BenefitsBlock variant="signin" />
      {error && (
        <LoyaltyErrorToast message={error} />
      )}
      <FieldsStack>
        <FloatingInput
          id="loginEmail"
          name="loginEmail"
          type="email"
          label="Email Address"
          value={email}
          onChange={onEmailChange}
          cross={email.length > 0}
          onFocus={onFocusFun}
          onBlur={onBlurFun}
          hasFocus={focusedInput === 'loginEmail'}
          resetClick={resetEmail}
          required={false}
          isWmoEnabled
        />
        <FloatingPasswordInput
          id="loginPassword"
          name="loginPassword"
          label="Password"
          value={password}
          onChange={onPasswordChange}
          cross={password.length > 0}
          onFocus={onFocusFun}
          onBlur={onBlurFun}
          hasFocus={focusedInput === 'loginPassword'}
          resetClick={resetPassword}
          required={false}
          isWmoEnabled
        />
        <ForgotPasswordLink href="/myaccount/login?forgotpassword=true">
          Forgot Password?
        </ForgotPasswordLink>
      </FieldsStack>

      {React.createElement(Captcha as any, {
        id: 'loyalty-signin-captcha',
        name: 'loyalty-signin-captcha',
        siteKey: captchaConfig.siteKey,
        setCaptcha,
        reset: false,
        // The Captcha wrapper handles both visible ('') and invisible modes
        // internally and resolves the invisible sitekey from captchaConfig
        // on its own (see @usap/uicomponents/src/components/Captcha.js).
        // Passing the ref here — not to a raw ReCAPTCHA — is what makes
        // resetCaptcha() / executeRecaptchaSync() in handleSubmit actually
        // resolve to functions.
        recaptchaType: isInvRecaptchaEnabled ? 'invisible' : '',
        ref: inVisibleRecaptchaRef,
      })}

      <PrimaryButton
        type="submit"
        disabled={!canSubmit || loading || (!isInvRecaptchaEnabled && !captcha)}
      >
        {loading ? 'Signing In…' : 'Sign In'}
      </PrimaryButton>

      <LoyaltySocialButtons
        buttonText="Continue With Google"
        form="Sign In"
        onEngagement={handleGoogleEngagement}
      />

      <Divider />

      <SectionHeading>Don&apos;t Have An Account?</SectionHeading>

      <PrimaryButton type="button" onClick={handleSwitchToCreate}>
        Create Account
      </PrimaryButton>
    </ModalBody>
  )
}

export default SignInForm
